<script lang="ts">
    import {
        KEY_CODE_ARROW_LEFT,
        KEY_CODE_ARROW_RIGHT,
        KEY_CODE_SPACE,
    } from "@/constants";
    import {
        INSTAGRAM_STORIES_MEDIA_IMAGE_TYPE,
        INSTAGRAM_STORIES_MEDIA_VIDEO_TYPE,
    } from "@apps/instagram/constants";
    import ArrowIcon from "@apps/instagram/widgets/instagram_gallery_widget/components/icons/ArrowIcon.svelte";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import { onMount, tick } from "svelte";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Icon from "@components/Icon.svelte";
    import CloseIcon from "@components/icons/CloseIcon.svelte";
    import Image from "@components/Image/Image.svelte";
    import Modal from "@components/Modal/Modal.svelte";
    import { ModalToken } from "@components/Modal/ModalManager";
    import { modalManagerContextKey } from "@components/Modal/ModalManager/constants";
    import Spinner from "@components/Spinner/Spinner.svelte";
    import VideoPlayerPauseIcon from "@components/VideoPlayer/icons/VideoPlayerPauseIcon.svelte";
    import VideoPlayerPlayIcon from "@components/VideoPlayer/icons/VideoPlayerPlayIcon.svelte";
    import VideoPlayerVolume0 from "@components/VideoPlayer/icons/VideoPlayerVolume0.svelte";
    import VideoPlayerVolume2 from "@components/VideoPlayer/icons/VideoPlayerVolume2.svelte";
    import { VideoController } from "@components/VideoPlayer/VideoController";

    import InstagramStoryModalImage from "./InstagramStoryModalImage.svelte";
    import InstagramStoryModalManager from "./InstagramStoryModalManager";
    import InstagramStoryModalProgress from "./InstagramStoryModalProgress.svelte";
    import InstagramStoryModalVideo from "./InstagramStoryModalVideo.svelte";

    export let instagramStoryModalManager: InstagramStoryModalManager;
    export let instagramStoriesModalToken: ModalToken<{ id: number }>;
    const pause$ = instagramStoryModalManager.pause$;
    const activeStory$ = instagramStoryModalManager.active$;
    const progress$ = instagramStoryModalManager.progress$;
    let activePlayPauseEffect: null | boolean = null;
    let videoSoundMuted = true;
    let modalElement: null | HTMLDivElement = null;
    const modalManager = getTypedContext(modalManagerContextKey);

    modalManager.addModal(instagramStoriesModalToken);
    const isOpen$ = modalManager.selectIsOpen(instagramStoriesModalToken);
    const modalParams$ = modalManager.selectParams(instagramStoriesModalToken);
    const showPlayPauseEffect = () => {
        activePlayPauseEffect = !activePlayPauseEffect;
    };
    const handleNext = () => {
        instagramStoryModalManager.next();
    };
    const handlePrev = () => {
        instagramStoryModalManager.prev();
    };
    const handleCloseModal = () => {
        modalManager.closeModal(instagramStoriesModalToken);
    };
    const handleKeydown = (e: KeyboardEvent) => {
        const key = e.which || e.keyCode;
        if (key === KEY_CODE_ARROW_LEFT) {
            handlePrev();
        } else if (key === KEY_CODE_ARROW_RIGHT) {
            handleNext();
        } else if (key === KEY_CODE_SPACE) {
            handleVideoPlayPause();
        }
    };
    const handlePlayerInitialized = (
        event: CustomEvent<{ videoController: VideoController }>
    ) => {
        instagramStoryModalManager.selectVideoController(
            event.detail.videoController
        );
    };
    const handleVideoSound = () => {
        videoSoundMuted = !videoSoundMuted;
    };
    const handleVideoPlayPause = () => {
        if (pause$.getValue()) {
            instagramStoryModalManager.playStory();
        } else {
            instagramStoryModalManager.pauseStory();
        }
    };
    const handleMediaLoaded = () => {
        instagramStoryModalManager.selectMediaLoaded();
    };
    const playStory = () => {
        instagramStoryModalManager.playStory();
        showPlayPauseEffect();
    };
    const pauseStory = () => {
        instagramStoryModalManager.pauseStory();
        showPlayPauseEffect();
    };
    onMount(() => {
        const modalParamsSubscription = modalParams$.subscribe((params) => {
            if (!params) return;
            instagramStoryModalManager.changeActiveStory(params.id);
        });
        return () => {
            modalParamsSubscription.unsubscribe();
            instagramStoryModalManager.destroy();
        };
    });
    modalManager.on("afterClose", () => {
        instagramStoryModalManager.resetProggres();
    });
    const setFocus = async () => {
        await tick();
        modalElement?.focus();
    };
    $: {
        $isOpen$;
        if ($isOpen$) {
            setTimeout(() => {
                setFocus();
            }, 0);
        }
    }
</script>

<Modal on:gw_modal_close={handleCloseModal} open={$isOpen$}>
    <BaseButton
        ariaLabel="Close instagram story modal"
        class="gw-ig-story-modal__close_btn"
        on:click={handleCloseModal}
    >
        <Icon width={24} height={24}>
            <CloseIcon />
        </Icon>
    </BaseButton>
    <div
        class="gw-ig-story-modal__view"
        tabindex="-1"
        bind:this={modalElement}
        on:keydown={handleKeydown}
    >
        <BaseButton
            on:mousedown={pauseStory}
            on:mouseup={playStory}
            class="gw-ig-story-modal__play-pause"
        />
        <div class="gw-ig-story-modal__content">
            <div class="gw-ig-story-modal__view-header">
                {#key $activeStory$}
                    <InstagramStoryModalProgress progress={$progress$} />
                {/key}
                {#if $activeStory$}
                    <div class="gw-ig-story-modal__view-header-panel">
                        <div class="gw-ig-story-modal__view-header-info">
                            <a
                                href={$activeStory$.objectLink}
                                class="gw-ig-story-modal__view-header-owner"
                            >
                                <Image
                                    src={$activeStory$.ownerImage}
                                    alt={`${$activeStory$.ownerUsername} profile photo`}
                                    wrapperClassName="gw-ig-story-modal__view-header-image"
                                />
                                <span>
                                    {$activeStory$.ownerUsername}
                                </span>
                            </a>
                        </div>
                        <div class="gw-ig-story-modal__view-header-controllers">
                            <BaseButton on:click={handleVideoPlayPause}>
                                <Icon width={14} height={14}>
                                    {#if $pause$}
                                        <VideoPlayerPlayIcon />
                                    {:else}
                                        <VideoPlayerPauseIcon />
                                    {/if}
                                </Icon>
                            </BaseButton>
                            <BaseButton on:click={handleVideoSound}>
                                <Icon width={14} height={14}>
                                    {#if videoSoundMuted}
                                        <VideoPlayerVolume0 />
                                    {:else}
                                        <VideoPlayerVolume2 />
                                    {/if}
                                </Icon>
                            </BaseButton>
                        </div>
                    </div>
                {/if}
            </div>
            <div class="gw-ig-story-modal__skeleton">
                <Spinner />
            </div>
            {#if $activeStory$ && $activeStory$.mediaType === INSTAGRAM_STORIES_MEDIA_IMAGE_TYPE}
                <InstagramStoryModalImage
                    story={$activeStory$}
                    on:imageLoaded={handleMediaLoaded}
                />
            {:else if $activeStory$ && $activeStory$.mediaType === INSTAGRAM_STORIES_MEDIA_VIDEO_TYPE}
                {#key $activeStory$}
                    <InstagramStoryModalVideo
                        story={$activeStory$}
                        on:initialized={handlePlayerInitialized}
                        {videoSoundMuted}
                    />
                {/key}
            {/if}
            <div
                class="gw-ig-story-modal__controllers"
                class:gw-ig-story-modal__play--active={activePlayPauseEffect}
            >
                <div class="gw-ig-story-modal__controller">
                    <Icon width={15} height={15}>
                        <VideoPlayerPlayIcon />
                    </Icon>
                </div>
            </div>
            <div
                class="gw-ig-story-modal__controllers"
                class:gw-ig-story-modal__pause--active={activePlayPauseEffect ===
                    false}
            >
                <div class="gw-ig-story-modal__controller">
                    <Icon width={15} height={15}>
                        <VideoPlayerPauseIcon />
                    </Icon>
                </div>
            </div>
        </div>
        <div>
            <BaseButton
                ariaLabel="Go to the previous story"
                class="gw-ig-story-modal-btn gw-ig-story-modal-btn--prev"
                on:click={handlePrev}
            >
                <Icon width={20} height={20}>
                    <ArrowIcon />
                </Icon>
            </BaseButton>
            <BaseButton
                BaseButtonariaLabel="Go to the next story"
                class="gw-ig-story-modal-btn gw-ig-story-modal-btn--next"
                on:click={handleNext}
            >
                <Icon width={20} height={20}>
                    <ArrowIcon />
                </Icon>
            </BaseButton>
        </div>
    </div>
</Modal>

<style>
    :global(.gw-ig-story-modal__close_btn) {
        position: absolute;
        z-index: 2;
        top: 0.5em;
        right: 0.5em;
        border: none;
        background: none;
        color: var(--gw-white-text-color);
        cursor: pointer;
    }
    .gw-ig-story-modal__view {
        position: relative;
        width: 90%;
        max-width: 412px;
        height: 100%;
        margin: 0 auto;
        outline: none;
        outline-offset: 0;
        box-shadow: none;
    }

    .gw-ig-story-modal__view * {
        outline: none;
        outline-offset: 0;
        box-shadow: none;
    }

    .gw-ig-story-modal__view-header {
        position: absolute;
        z-index: 1;
        top: 5px;
        box-sizing: border-box;
        width: 100%;
        padding: 0 10px;
    }

    .gw-ig-story-modal__view-header-panel {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 8px;
    }

    .gw-ig-story-modal__view-header-owner {
        display: flex;
        align-items: center;
        column-gap: 8px;
        color: var(--gw-white-text-color);
        font-size: 0.8em;
        font-weight: 500;
        text-decoration: none;
        text-transform: lowercase;
    }

    .gw-ig-story-modal__view-header
        :global(.gw-ig-story-modal__view-header-image) {
        width: 32px;
        height: 32px;
        border-radius: 50%;
    }

    .gw-ig-story-modal__skeleton {
        --gw-spinner-size: 40px;
        --gw-spinner-color: #fff;

        display: flex;
        position: absolute;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        background-color: #000;
    }

    .gw-ig-story-modal__view-header-controllers {
        display: flex;
        align-items: center;
        column-gap: 10px;
    }

    .gw-ig-story-modal__content {
        height: 100%;
    }

    :global(.gw-ig-story-modal-btn) {
        display: flex;
        position: absolute;
        z-index: 1;
        top: 50%;
        padding: 6px;
        transform: translateY(-50%);
        border-radius: 50%;
        opacity: 0.6;
        background: var(--gw-dark-bg);
        color: var(--gw-white-text-color);
    }

    :global(.gw-ig-story-modal-btn--prev) {
        left: -60px;
        transform: translateY(-50%) rotateZ(180deg);
    }

    :global(.gw-ig-story-modal-btn--next) {
        right: -60px;
    }

    :global(.gw-ig-story-modal__play-pause) {
        position: absolute;
        z-index: 1;
        width: 100%;
        height: 100%;
        opacity: 0.5;
    }

    .gw-ig-story-modal__pause--active {
        animation: playPause 0.5s linear 1 normal forwards;
    }

    .gw-ig-story-modal__play--active {
        animation: playPause 0.5s linear 1 normal forwards;
    }

    .gw-ig-story-modal__controllers {
        display: flex;
        position: absolute;
        top: 50%;
        left: 50%;
        align-items: center;
        justify-content: center;
        width: 52px;
        height: 52px;
        margin-top: -26px;
        margin-left: -26px;
        border-radius: 50%;
        opacity: 0;
        background: rgba(0, 0, 0, 0.5);
    }

    .gw-ig-story-modal__controller {
        display: flex;
    }

    @keyframes playPause {
        0% {
            opacity: 1;
        }

        to {
            transform: scale(2);
            opacity: 0;
        }
    }

    @media all and (max-width: 560px) {
        :global(.gw-ig-story-modal-btn--prev) {
            left: 0;
        }

        :global(.gw-ig-story-modal-btn--next) {
            right: 0;
        }
    }
</style>
